/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 컴포넌트 내부의 구성요소 클래스를 정의한다.
 *
 * @file /src/components/menu/elements/Item.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import * as parent from '@base/Element';
import { Dom } from '@utils/Dom';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 메뉴아이템이 보일 때
     */
    show?: (item: T) => void;

    /**
     * 메뉴아이템이 숨겨질 때
     */
    hide?: (item: T) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 메뉴타입 (기본값은 button, 메뉴 아이템이 늘어난다면 추가)
     */
    type: 'button' | 'filter' | 'custom';

    /**
     * 메뉴명
     */
    name?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Item extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'menuitem';

    /**
     * 메뉴명
     */
    name: string = null;

    /**
     * 메뉴아이템 타입
     * 각 메뉴아이템별 엘리먼트 클래스에서 정의
     */
    abstract variant: string;

    /**
     * 메뉴아이템 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Item> = null) {
        super(properties);

        this.name = properties?.name ?? null;
    }

    /**
     * 엘리먼트의 DOM 을 가져온다.
     * 엘리먼트를 컴포넌트에 append 한 뒤 render() 함수를 호출하여 실제 엘리먼트의 DOM 을 처리한다.
     *
     * @returns 엘리먼트의 DOM 구조
     */
    $getElement(): Dom {
        if (this.name !== null) {
            super.$getElement().setAttr('data-name', this.name);
        }

        return super.$getElement();
    }

    /**
     * 메뉴 아이템에 포커스를 지정한다.
     *
     * @returns 포커스가능여부 (false 인 경우 포커스가 불가능한 메뉴아이템)
     */
    abstract focus(): boolean;

    /**
     * 메뉴 아이템 내부에 포커스를 가진 DOM 이 존재하는지 확인한다.
     *
     * @returns 포커스존재여부
     */
    isFocused(): boolean {
        return this.isRendered() == true && this.$getElement().getEl().contains(document.activeElement);
    }
}
